import { Tabs } from '@/components/DesignSystem';
import {
  useDefaultRetailerId,
  withRetailerIdParam,
} from '@/components/shared/DefaultRetailerSelector';
import { getTabBarSx } from '@/components/shared/styles/cardTabBar';
import { Routes } from '@/constants/Routes';
import { Tab, useTheme } from '@mui/material';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

export enum DebugTab {
  Metrics = 'metrics',
  PointsAudit = 'pointsAudit',
  UserAccounts = 'userAccounts',
  UserInspector = 'userInspector',
  UxTesting = 'uxTesting',
}

export const debugTabRoutes: Record<DebugTab, Routes> = {
  [DebugTab.Metrics]: Routes.ADMIN_METRICS,
  [DebugTab.PointsAudit]: Routes.ADMIN_POINTS_AUDIT,
  [DebugTab.UserAccounts]: Routes.ADMIN_USER_ACCOUNTS,
  [DebugTab.UserInspector]: Routes.ADMIN_USER_INSPECTOR,
  [DebugTab.UxTesting]: Routes.UX_TESTING,
};

const retailerFilteredTabs = [DebugTab.Metrics, DebugTab.PointsAudit];

const tabOrder = [
  DebugTab.PointsAudit,
  DebugTab.Metrics,
  DebugTab.UserInspector,
  DebugTab.UserAccounts,
  DebugTab.UxTesting,
];

interface DebugPageProps {
  children: ReactNode,
  tab: DebugTab,
}

export const DebugPage = ({ children, tab }: DebugPageProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { defaultRetailerId } = useDefaultRetailerId();

  const getTabPath = (debugTab: DebugTab) => (
    defaultRetailerId && retailerFilteredTabs.includes(debugTab)
      ? withRetailerIdParam(debugTabRoutes[debugTab], defaultRetailerId)
      : debugTabRoutes[debugTab]
  );

  return (
    <>
      <Tabs
        onChange={(_, value: DebugTab) => navigate(getTabPath(value))}
        sx={{ ...getTabBarSx(theme), px: 2 }}
        testID="debug-tabs"
        value={tab}
      >
        {tabOrder.map((debugTab) => (
          <Tab
            data-testid={`debug-tab-${debugTab}`}
            key={debugTab}
            label={t($ => $.debug.tabs[debugTab])}
            value={debugTab}
          />
        ))}
      </Tabs>
      {children}
    </>
  );
};

/** Sends bookmarks of a page's address from before the Debug page to its tab. */
export const LegacyDebugRedirect = ({ to }: { to: Routes }) => {
  const { search } = useLocation();
  return <Navigate replace to={{ pathname: to, search }} />;
};
